import { TransactionSummaryModal } from "../transactions/TransactionSummaryModal";
import { ApiError } from "@carepass/api-client";
import { useCallback, useState } from "react";
import { useParams } from "react-router";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { canAccessAdminPage } from "../../app/permissions";
import { ADMIN_ROUTES_BY_SCREEN, parseAdminEntityId, pathForAdminEntityScreen, pathForAdminScreen } from "../../app/routes";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { MemberDetailView } from "./components/MemberDetailView";
import type { MemberDetailTab } from "./components/MemberDetailView";
import {
  useCreateMemberNote,
  useMemberDetail,
  useMemberHistory,
  useMemberNotes,
  useMemberPayments,
  useMemberTerminationPreview,
  useRequestMemberTermination,
} from "./queries";
import type { Schemas } from "../workspace/api.generated";

function detailState(query: ReturnType<typeof useMemberDetail>): "ready" | "loading" | "error" | "not-found" {
  if (query.isPending) return "loading";
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found";
  if (query.isError) return "error";
  return "ready";
}

function MemberDetailPageForMember({ memberId }: { readonly memberId: number }) {
  const session = useAdminSession();
  const { apiClient } = useAdminApplication();
  const terminationBanks = useCallback(async () => (await apiClient.get<{code: string; name: string}[]>("/api/v1/admin/members/termination/banks")).data, [apiClient]);
  const currentTermination = useCallback(async () => (await apiClient.get<Schemas["RefundView"] | undefined>(`/api/v1/admin/members/${memberId}/termination/current`, {cache: "no-store"})).data || undefined, [apiClient, memberId]);
  const verifyTerminationAccount = useCallback(async (input: {bankCode: string; accountNumber: string; quoteToken: string}) => (await apiClient.post<{accountHolder: string; verificationToken: string}, typeof input>(`/api/v1/admin/members/${memberId}/termination/account-verification`, input)).data, [apiClient, memberId]);
  const retryTermination = useCallback(async (refundId: number) => (await apiClient.post<Schemas["RefundView"], Record<string, never>>(`/api/v1/admin/refunds/${refundId}/execute`, {})).data, [apiClient]);
  const navigation = useAdminNavigation({ contextKey: "members", defaultListPath: `${pathForAdminScreen("05")}?page=0&size=10`, originLabel: "회원 상세" });
  const [transactionId, setTransactionId] = useState<number | null>(null);
  const [tab, setTab] = useState<MemberDetailTab>("기본정보");
  const [paymentPage, setPaymentPage] = useState(0);
  const detail = useMemberDetail(memberId);
  const payments = useMemberPayments(memberId, { page: paymentPage, size: 10 }, tab === "월정액 결제");
  const notes = useMemberNotes(memberId, tab === "상담 메모");
  const history = useMemberHistory(memberId, ["약관 동의", "거래 내역", "알림 이력"].includes(tab));
  const createNote = useCreateMemberNote(memberId);
  const termination = useRequestMemberTermination(memberId);
  const terminationPreview = useMemberTerminationPreview(memberId);
  const previewTermination = useCallback(async () => {
    const result = await terminationPreview.refetch();
    if (result.error) throw result.error;
    if (!result.data) throw new Error("환불 견적을 불러오지 못했습니다.");
    return result.data;
  }, [terminationPreview.refetch]);
  const authorities = session.authSession?.authorities ?? [];
  const has = (authority: string) => authorities.includes(authority);
  const openRefunds = canAccessAdminPage(
    ADMIN_ROUTES_BY_SCREEN["11"].id,
    authorities,
    "operations",
  );

  const selectTab = (nextTab: MemberDetailTab) => {
    setTab(nextTab);
    if (nextTab !== "월정액 결제") setPaymentPage(0);
  };

  const refreshDetail = async (): Promise<Schemas["MemberDetailView"] | undefined> => {
    const result = await detail.refetch();
    if (result.error) throw result.error;
    return result.data;
  };

  const terminateMember = async (input: Schemas["RequestTerminationRequest"]) => {
    try {
      return await termination.mutateAsync(input);
    } finally {
      termination.reset();
    }
  };

  return (
    <>
    <MemberDetailView
      onViewTransaction={setTransactionId}
      creatingNote={createNote.isPending}
      detail={detail.data}
      key={memberId}
      notes={notes.data}
      notesState={notes.isError ? "error" : notes.isPending && tab === "상담 메모" ? "loading" : "ready"}
      history={history.data}
      historyState={history.isError ? "error" : history.isPending && ["약관 동의", "거래 내역", "알림 이력"].includes(tab) ? "loading" : "ready"}
      onBack={navigation.backToList}
      backHref={navigation.backPath}
      backLabel={navigation.backLabel}
      detailHref={pathForAdminEntityScreen("06", memberId)}
      refundsHref={`${pathForAdminScreen("11")}?memberId=${memberId}&page=0&size=20`}
      onCreateNote={(input) => createNote.mutateAsync(input)}
      onOpenRefunds={() => navigation.openRelated(`${pathForAdminScreen("11")}?memberId=${memberId}&page=0&size=20`, "회원 상세")}
      onPaymentPageChange={setPaymentPage}
      onRefreshDetail={refreshDetail}
      onRetryDetail={() => { void detail.refetch(); }}
      onRetryNotes={() => { void notes.refetch(); }}
      onRetryHistory={() => { void history.refetch(); }}
      onRetryPayments={() => { void payments.refetch(); }}
      onTabChange={selectTab}
      onTerminationBanks={terminationBanks}
      onCurrentTermination={currentTermination}
      onVerifyTerminationAccount={verifyTerminationAccount}
      onRetryTermination={retryTermination}
      onTerminate={terminateMember}
      onPreviewTermination={previewTermination}
      payments={payments.data}
      paymentsState={payments.isError ? "error" : payments.isPending && tab === "월정액 결제" ? "loading" : "ready"}
      permissions={{
        revealSensitive: has("MEMBER_SENSITIVE_READ"),
        createNote: has("MEMBER_CARE_NOTE_CREATE"),
        requestTermination: has("MEMBER_TERMINATE_REQUEST"),
        openRefunds,
        cancelPayment: has("PAYMENT_CANCEL"),
        executeTransfer: has("TRANSFER_EXECUTE"),
      }}
      state={detailState(detail)}
      tab={tab}
      terminating={termination.isPending}
    />
    {transactionId !== null ? <TransactionSummaryModal key={transactionId} transactionId={transactionId} onClose={() => setTransactionId(null)} /> : null}
    </>
  );
}

export function MemberDetailPage() {
  const memberId = parseAdminEntityId(useParams().memberId);
  if (memberId === null) return null;
  return <MemberDetailPageForMember key={memberId} memberId={memberId} />;
}
